import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ThemePreference = "classic" | "dark" | "auto";

const THEME_CHOSEN_KEY = "tecly_theme_chosen";

const applyTheme = (isDark: boolean) => {
  document.documentElement.classList.toggle("dark", isDark);
};

const isNightTime = () => {
  const hour = new Date().getHours();
  return hour < 6 || hour >= 18;
};

const getThemeChosenKey = (userId: string) => `${THEME_CHOSEN_KEY}_${userId}`;

export const useTheme = () => {
  const [preference, setPreference] = useState<ThemePreference>("classic");
  const [userId, setUserId] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [ready, setReady] = useState(false);

  const applyPreference = useCallback((pref: ThemePreference) => {
    if (pref === "dark") {
      applyTheme(true);
    } else if (pref === "classic") {
      applyTheme(false);
    } else {
      applyTheme(isNightTime());
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        const { data } = await supabase
          .from("profiles")
          .select("theme_preference")
          .eq("user_id", session.user.id)
          .maybeSingle();
        const pref = (data?.theme_preference as ThemePreference) || "classic";
        setPreference(pref);
        applyPreference(pref);

        // Show onboarding only once per user (stored per user_id)
        const chosen = localStorage.getItem(getThemeChosenKey(session.user.id));
        // Also check legacy key for backward compat
        const legacyChosen = localStorage.getItem(THEME_CHOSEN_KEY);
        if (!chosen && !legacyChosen) {
          // Only show if theme is still default (classic) - means user never chose
          if (pref === "classic" && !data?.theme_preference) {
            setShowOnboarding(true);
          }
        }
      }
      setReady(true);
    };
    init();
  }, [applyPreference]);

  useEffect(() => {
    if (preference !== "auto") return;
    const interval = setInterval(() => applyPreference("auto"), 60000);
    return () => clearInterval(interval);
  }, [preference, applyPreference]);

  const setTheme = async (pref: ThemePreference) => {
    setPreference(pref);
    applyPreference(pref);
    // Mark theme as chosen for this specific user
    if (userId) {
      localStorage.setItem(getThemeChosenKey(userId), "true");
    }
    localStorage.setItem(THEME_CHOSEN_KEY, "true");
    setShowOnboarding(false);
    if (userId) {
      await supabase
        .from("profiles")
        .update({ theme_preference: pref })
        .eq("user_id", userId);
    }
  };

  return { preference, setTheme, showOnboarding, ready };
};
